import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';

import { combineLatest } from 'rxjs';

import { RoleAccessDirective } from '../../../@core/directives/role-access.directive';
import { UserApiActions } from '../../../@store/user/user.actions';
import {
  selectFetchedUser,
  selectFetchedUserError,
  selectFetchedUserLoading,
  selectFetchedUserRoles,
  selectFetchedUserRolesError,
  selectFetchedUserRolesLoading,
  selectUpdatedUserError,
  selectUpdatedUserSuccess,
} from '../../../@store/user/user.selectors';
import { CONFIG } from '../../../config';
import { SafeAny } from '../../../types/safe-any';

@Component({
  selector: 'ciwc-edit-user',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    RoleAccessDirective,
  ],
  templateUrl: './edit-user.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditUserComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  readonly updatedUserError = this.store.selectSignal(selectUpdatedUserError);
  readonly userLoading = this.store.selectSignal(selectFetchedUserLoading);
  readonly userError = this.store.selectSignal(selectFetchedUserError);
  readonly userRolesLoading = this.store.selectSignal(selectFetchedUserRolesLoading);
  readonly userRolesError = this.store.selectSignal(selectFetchedUserRolesError);
  readonly updatedUserSuccess = this.store.selectSignal(selectUpdatedUserSuccess);
  readonly CONFIG = CONFIG;

  isEditing = signal(false);
  roles: SafeAny[] = [];
  user: SafeAny = {};
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;

  userForm = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    title: ['', Validators.required],
    faxNumber: ['', Validators.required],
    roles: ['', Validators.required],
  });

  constructor() {
    effect(() => {
      if (this.updatedUserError()) {
        this.isEditing.set(false);
      }

      if (this.updatedUserSuccess()) {
        this.isEditing.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.fetchUser();
    this.fetchUserRoles();
    this.initValues();
  }

  onSubmit(): void {
    this.isEditing.set(true);

    const payload = this.userForm.getRawValue();
    const userId = this.route.snapshot.paramMap.get('id');

    if (payload.roles) {
      payload.roles = [payload.roles] as SafeAny;
    }

    if (userId) {
      this.store.dispatch(UserApiActions.updateUser({ payload, userId }));
    }
  }

  goBack(): void {
    const roles = localStorage.getItem('roles');

    if (roles === this.CONFIG.ROLES.ORDERS_MANAGER || roles === this.CONFIG.ROLES.ORDERS_ADMIN) {
      this.router.navigate(['/']);
    } else {
      this.router.navigate(['/users']);
    }
  }

  fetchUser(): void {
    const userId = Number(this.route.snapshot.paramMap.get('id'));

    if (userId) {
      this.store.dispatch(UserApiActions.fetchUser({ userId: userId.toString() }));
    }
  }

  fetchUserRoles(): void {
    this.store.dispatch(UserApiActions.fetchUserRoles({ payload: { identifier: 'USERS_ROLES' } }));
  }

  initValues(): void {
    combineLatest([this.store.select(selectFetchedUser), this.store.select(selectFetchedUserRoles)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, roles]: [SafeAny, SafeAny]) => {
        if (!user || !roles) return;

        this.roles = roles;
        this.user = user;

        this.userForm.patchValue({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          title: user.title,
          faxNumber: user.faxNumber,
          roles: roles.find((role: SafeAny) => role.key === user.roles[0]).key,
        });
      });
  }
}
